import React from 'react';
import styled from 'styled-components';

interface Props {
    onBack: () => void;
    onSave?: () => void;
    canSave?: boolean;
    backAriaLabel?: string;
    saveLabel?: string;
    className?: string;
}

const BackGlyph = () => (
    <svg
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth={2.2}
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden
    >
        <polyline points='15 18 9 12 15 6' />
    </svg>
);

const CheckGlyph = () => (
    <svg
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth={2.4}
        strokeLinecap='round'
        strokeLinejoin='round'
        aria-hidden
    >
        <polyline points='20 6 9 17 4 12' />
    </svg>
);

const TeudaActionDock: React.FC<Props> = ({
    onBack,
    onSave,
    canSave = false,
    backAriaLabel = 'חזרה',
    saveLabel = 'שמור',
    className,
}) => (
    <Host className={className}>
        <Glass>
            {canSave && onSave && (
                <SavePill type='button' onClick={onSave}>
                    <CheckGlyph />
                    <span>{saveLabel}</span>
                </SavePill>
            )}
            <BackGhost type='button' aria-label={backAriaLabel} onClick={onBack}>
                <BackGlyph />
            </BackGhost>
        </Glass>
    </Host>
);

export default TeudaActionDock;

const Host = styled.div`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: max(1.25rem, calc(env(safe-area-inset-bottom) + 0.75rem));
    z-index: 50;
    pointer-events: none;
`;

const Glass = styled.div`
    pointer-events: auto;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    border-radius: 1.75rem;
    background: linear-gradient(
        180deg,
        rgba(255, 255, 255, 0.18) 0%,
        rgba(255, 255, 255, 0.03) 38%,
        rgba(255, 255, 255, 0.02) 65%,
        rgba(255, 255, 255, 0.08) 100%
    );
    border: 1px solid rgba(255, 255, 255, 0.28);
    box-shadow:
        inset 0 1px 0 rgba(255, 255, 255, 0.55),
        inset 0 -0.5px 0 rgba(255, 255, 255, 0.06),
        0 8px 24px rgba(0, 0, 0, 0.18),
        0 24px 56px rgba(0, 0, 0, 0.12);
    backdrop-filter: blur(40px) saturate(180%);
    -webkit-backdrop-filter: blur(40px) saturate(180%);
`;

const BackGhost = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.375rem;
    height: 2.375rem;
    padding: 0;
    border: 0;
    border-radius: 50%;
    background: transparent;
    color: ${({ theme }) => theme.colors.ink2};
    cursor: pointer;
    transition:
        background ${({ theme }) => theme.motion.fast} ease,
        color ${({ theme }) => theme.motion.fast} ease,
        transform ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1.125rem;
        height: 1.125rem;
    }

    &:hover {
        background: rgba(255, 255, 255, 0.45);
        color: ${({ theme }) => theme.colors.ink};
    }

    &:active {
        transform: scale(0.96);
    }
`;

const SavePill = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    height: 2.375rem;
    padding: 0 1rem;
    border: 0;
    border-radius: 1.0625rem;
    background: ${({ theme }) => theme.colors.accent};
    color: ${({ theme }) => theme.colors.ink};
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.md};
    font-weight: 600;
    letter-spacing: 0.01em;
    cursor: pointer;
    user-select: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
    transition:
        transform ${({ theme }) => theme.motion.fast} ease,
        background ${({ theme }) => theme.motion.fast} ease,
        box-shadow ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem;
        height: 1rem;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accentDeep};
        box-shadow: ${({ theme }) => theme.shadows.submitHover};
    }

    &:active {
        transform: scale(0.98);
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;
